/**
 * @fileoverview Landing page navbar component - Light futuristic design
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export const LandingNavbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t, i18n } = useTranslation();

  const languages = [
    { code: 'es', label: 'Español' },
    { code: 'eu', label: 'Euskera' },
    { code: 'en', label: 'English' },
    { code: 'it', label: 'Italiano' },
    { code: 'fr', label: 'Français' },
    { code: 'de', label: 'Deutsch' },
    { code: 'pt', label: 'Português' }
  ];

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    closeMenu();
  };

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const [isLangOpen, setIsLangOpen] = useState(false);

  const toggleLang = () => setIsLangOpen(!isLangOpen);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setIsLangOpen(false); // Reset lang dropdown when menu closes
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsLangOpen(false);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 pt-[env(safe-area-inset-top)] ${scrolled
          ? 'bg-white/95 backdrop-blur-xl shadow-lg shadow-black/5'
          : 'bg-white/80 backdrop-blur-sm'
          }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 flex items-center justify-between h-20">

          {/* Mobile: Hamburger on the left */}
          <div className="flex items-center gap-3 lg:hidden">
            <button
              className="text-gray-800 text-xl p-2 -ml-2"
              onClick={toggleMenu}
              aria-label="Toggle menu"
            >
              <i className={isMenuOpen ? 'fas fa-times' : 'fas fa-bars'}></i>
            </button>

            {/* Logo - Smaller on mobile */}
            <a href="/" className="flex items-center" onClick={closeMenu}>
              <img
                src="/logo_transparente.webp"
                alt="4tlayer"
                className="h-8 sm:h-12 object-contain"
              />
            </a>
          </div>

          {/* Desktop Logo (hidden on mobile) */}
          <a href="/" className="hidden lg:flex items-center">
            <img
              src="/logo_transparente.webp"
              alt="4tlayer"
              className="h-12 object-contain"
            />
          </a>

          {/* Desktop nav links */}
          <div className="hidden lg:flex items-center gap-7">
            <a href="#especializacion" className="text-gray-500 hover:text-brand-electric-blue font-montserrat font-semibold text-sm tracking-wide transition-colors duration-300 relative group">
              {t('nav.specialization')}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-brand-electric-blue group-hover:w-full transition-all duration-300"></span>
            </a>
            <a href="#beneficios" className="text-gray-500 hover:text-brand-electric-blue font-montserrat font-semibold text-sm tracking-wide transition-colors duration-300 relative group">
              {t('nav.features')}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-brand-electric-blue group-hover:w-full transition-all duration-300"></span>
            </a>
            <a href="#como-funciona" className="text-gray-500 hover:text-brand-electric-blue font-montserrat font-semibold text-sm tracking-wide transition-colors duration-300 relative group">
              {t('nav.howItWorks')}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-brand-electric-blue group-hover:w-full transition-all duration-300"></span>
            </a>
            {/* <a href="#nosotros" className="text-gray-500 hover:text-brand-electric-blue font-montserrat font-semibold text-sm tracking-wide transition-colors duration-300 relative group">
              {t('nav.team')}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-brand-electric-blue group-hover:w-full transition-all duration-300"></span>
            </a> */}
            <a
              href="#contacto"
              className="px-2 py-2 text-brand-electric-blue hover:text-blue-700 font-bold text-sm transition-all duration-300 items-center gap-2"
            >
              <span>{t('nav.contact')}</span>
            </a>
          </div>

          {/* Auth buttons, Lang Selector */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Language Selector Desktop */}
            <div className="hidden lg:block relative group mr-2">
              <button className="flex items-center gap-2 text-gray-500 hover:text-brand-electric-blue font-montserrat font-bold text-xs uppercase tracking-widest transition-all duration-300 bg-gray-50 px-3 py-1.5 rounded-full border border-gray-100">
                <i className="fas fa-globe-americas"></i>
                {i18n.language ? i18n.language.split('-')[0].toUpperCase() : '--'}
                <i className="fas fa-chevron-down text-[10px] opacity-50"></i>
              </button>
              <div className="absolute top-full right-0 mt-2 w-48 bg-white border border-gray-100 rounded-2xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50 overflow-hidden py-1">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`w-full text-left px-5 py-2.5 text-sm font-montserrat font-medium transition-colors ${(i18n.language && i18n.language.startsWith(lang.code)) ? 'text-brand-electric-blue bg-brand-electric-blue/5' : 'text-gray-600 hover:bg-gray-50'
                      }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Login button: Blue on desktop */}
            <a
              href="https://app.truedok.com/login"
              className="hidden lg:flex px-5 py-2.5 rounded-lg bg-brand-electric-blue text-white font-semibold text-sm hover:bg-blue-600 transition-all duration-300 items-center gap-2 shadow-lg shadow-brand-electric-blue/20"
            >
              <i className="fas fa-user text-[10px]"></i>
              {t('nav.login')}
            </a>
          </div>

        </div>
      </nav>

      {/* === MOBILE MENU: OUTSIDE <nav> to avoid backdrop-blur inheritance === */}

      {/* Dark backdrop overlay */}
      <div
        className={`fixed inset-0 bg-black/60 z-[60] transition-opacity duration-300 lg:hidden ${isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={closeMenu}
      />

      {/* Drawer panel - completely independent, solid white */}
      <div
        className={`fixed top-0 left-0 w-72 h-[100dvh] z-[70] p-6 pt-[calc(1.5rem+env(safe-area-inset-top))] pb-[calc(1.5rem+env(safe-area-inset-bottom))] transform transition-transform duration-300 shadow-2xl lg:hidden overflow-y-auto ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ backgroundColor: '#ffffff' }}
      >
        <div className="flex items-center justify-between mb-8">
          <a href="/" onClick={closeMenu}>
            <img
              src="/logo_transparente.webp"
              alt="4tlayer"
              className="h-10 object-contain"
            />
          </a>
          <button className="text-gray-400 hover:text-gray-700 text-xl" onClick={closeMenu}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Primary Mobile Action at Top */}
        <div className="flex flex-col gap-3 mb-8">
          <a href="https://app.truedok.com/login" className="py-4 px-4 rounded-2xl bg-brand-electric-blue text-white text-center font-bold hover:bg-blue-600 transition-all shadow-lg shadow-brand-electric-blue/20 flex items-center justify-center gap-2" onClick={closeMenu}>
            <i className="fas fa-user"></i>
            {t('nav.login')}
          </a>
        </div>

        <div className="flex flex-col gap-1 mb-8">
          <a href="#especializacion" className="flex items-center gap-4 text-gray-600 hover:text-brand-electric-blue py-3 px-4 rounded-xl hover:bg-brand-electric-blue/5 transition-all font-semibold" onClick={closeMenu}>
            <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-gray-400">
              <i className="fas fa-award"></i>
            </div>
            {t('nav.specialization')}
          </a>
          <a href="#beneficios" className="flex items-center gap-4 text-gray-600 hover:text-brand-electric-blue py-3 px-4 rounded-xl hover:bg-brand-electric-blue/5 transition-all font-semibold" onClick={closeMenu}>
            <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-gray-400">
              <i className="fas fa-shield-alt"></i>
            </div>
            {t('nav.features')}
          </a>
          <a href="#como-funciona" className="flex items-center gap-4 text-gray-600 hover:text-brand-electric-blue py-3 px-4 rounded-xl hover:bg-brand-electric-blue/5 transition-all font-semibold" onClick={closeMenu}>
            <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-gray-400">
              <i className="fas fa-cogs"></i>
            </div>
            {t('nav.howItWorks')}
          </a>
          {/* <a href="#nosotros" className="flex items-center gap-4 text-gray-600 hover:text-brand-electric-blue py-3 px-4 rounded-xl hover:bg-brand-electric-blue/5 transition-all font-semibold" onClick={closeMenu}>
            <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-gray-400">
              <i className="fas fa-users"></i>
            </div>
            {t('nav.team')}
          </a> */}
          <a href="#contacto" className="flex items-center gap-4 text-brand-electric-blue hover:text-blue-700 py-3 px-4 rounded-xl bg-brand-electric-blue/5 transition-all font-bold" onClick={closeMenu}>
            <div className="w-8 h-8 rounded-lg bg-brand-electric-blue/10 flex items-center justify-center text-brand-electric-blue">
              <i className="fas fa-paper-plane"></i>
            </div>
            {t('nav.contact')}
          </a>

          {/* Language Selector Mobile Dropdown - Integrated */}
          <div className="mt-2">
            <button
              onClick={toggleLang}
              className="w-full flex items-center justify-between p-4 bg-gray-50 border border-gray-100 rounded-2xl transition-all"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-brand-electric-blue/10 flex items-center justify-center text-brand-electric-blue text-xs font-bold">
                  <i className="fas fa-globe-americas"></i>
                </div>
                <div className="text-left">
                  <div className="text-[10px] font-audiowide text-gray-400 uppercase tracking-widest leading-none mb-1">Idioma</div>
                  <div className="text-xs font-montserrat font-bold text-[#111111]">
                    {languages.find(l => l.code === (i18n.language ? i18n.language.split('-')[0] : 'es'))?.label || 'Select Language'}
                  </div>
                </div>
              </div>
              <i className={`fas fa-chevron-down text-xs text-gray-400 transition-transform duration-300 ${isLangOpen ? 'rotate-180' : ''}`}></i>
            </button>

            <div className={`overflow-hidden transition-all duration-300 ${isLangOpen ? 'max-h-[250px] mt-2 opacity-100' : 'max-h-0 opacity-0'}`}>
              <div className="bg-gray-50/50 border border-gray-100 rounded-2xl overflow-y-auto py-1 custom-scrollbar" style={{ maxHeight: '240px' }}>
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-xs font-montserrat font-semibold transition-all ${(i18n.language && i18n.language.startsWith(lang.code)) ? 'text-brand-electric-blue bg-brand-electric-blue/5' : 'text-gray-500 hover:bg-gray-100'
                      }`}
                  >
                    {lang.label}
                    {(i18n.language && i18n.language.startsWith(lang.code)) && <i className="fas fa-check ml-auto text-[10px]"></i>}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

      </div>
    </>
  );
};
